import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  FolderOpen,
  Users,
  Calendar,
  MoreVertical,
  Clock,
  CheckCircle2,
  ChevronDown,
  Folder,
  SlidersHorizontal,
  Save,
  Star,
  FolderClosed,
  Archive,
  ArrowRight,
} from "lucide-react";
import { useProject } from "../contexts/ProjectContext";
import { useUser } from "../contexts/UserContext";
import { useSocket } from "../contexts/SocketContext";
import CreateProjectModal from "./CreateProjectModal";
import Avatar from "./Avatar";
import { RunningTasksList } from "./TimeTracking";
import { isEmptyHtml, getCleanTextPreview } from "../utils/htmlUtils";
import {
  getProjectStatusColors,
  getProjectTypeColors,
  getStatusBadgeClasses,
} from "../utils/statusColors";
import { cardAPI, userAPI, timeEntryAPI } from "../utils/api";
import { features } from "../config/features";

const ProjectList = () => {
  const { projects, loading, fetchProjects } = useProject();
  const { user } = useUser();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [cardsDueToday, setCardsDueToday] = useState([]);
  const [cardsBackDate, setCardsBackDate] = useState([]);
  const [cardsUpcoming, setCardsUpcoming] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [loadingBackDateCards, setLoadingBackDateCards] = useState(true);
  const [loadingUpcomingCards, setLoadingUpcomingCards] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [sortOrder, setSortOrder] = useState("recent"); // "recent" or "oldest"
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const sortDropdownRef = useRef(null);
  // Pinned projects (stored server-side per user)
  const [pinnedIds, setPinnedIds] = useState([]);

  // Running tasks
  const [runningTasks, setRunningTasks] = useState([]);
  const [loadingRunningTasks, setLoadingRunningTasks] = useState(false);
  const { socket, joinProject, leaveProject } = useSocket();

  // Fetch functions for dashboard cards
  const fetchCardsDueToday = async () => {
    try {
      setLoadingCards(true);
      console.log("📅 Fetching cards due today...");
      const response = await cardAPI.getCardsDueToday();
      console.log("📅 Cards due today response:", response.data);
      setCardsDueToday(response.data.cards || []);
    } catch (error) {
      console.error("❌ Error fetching cards due today:", error);
      setCardsDueToday([]);
    } finally {
      setLoadingCards(false);
    }
  };

  const fetchCardsBackDate = async () => {
    try {
      setLoadingBackDateCards(true);
      console.log("📅 Fetching cards back date...");
      const response = await cardAPI.getCardsBackDate();
      console.log("📅 Cards back date response:", response.data);
      setCardsBackDate(response.data.cards || []);
    } catch (error) {
      console.error("❌ Error fetching cards back date:", error);
      setCardsBackDate([]);
    } finally {
      setLoadingBackDateCards(false);
    }
  };

  const fetchCardsUpcoming = async () => {
    try {
      setLoadingUpcomingCards(true);
      console.log("📅 Fetching upcoming cards...");
      const response = await cardAPI.getCardsUpcoming();
      console.log("📅 Upcoming cards response:", response.data);
      setCardsUpcoming(response.data.cards || []);
    } catch (error) {
      console.error("❌ Error fetching upcoming cards:", error);
      setCardsUpcoming([]);
    } finally {
      setLoadingUpcomingCards(false);
    }
  };

  const fetchAllDashboardCards = () => {
    if (user) {
      fetchCardsDueToday();
      fetchCardsBackDate();
      fetchCardsUpcoming();
    }
  };

  // Join all project rooms to receive card updates
  useEffect(() => {
    if (socket && projects && projects.length > 0) {
      projects.forEach((project) => {
        joinProject(project._id);
      });

      return () => {
        projects.forEach((project) => {
          leaveProject(project._id);
        });
      };
    }
  }, [socket, projects, joinProject, leaveProject]);

  // Fetch running tasks
  const fetchRunningTasks = async () => {
    if (!features.timeTracking) return;

    try {
      setLoadingRunningTasks(true);
      const response = await timeEntryAPI.getRunningTasks();
      if (response.data?.success) {
        setRunningTasks(response.data.runningTasks || []);
      }
    } catch (error) {
      console.error("Failed to fetch running tasks:", error);
    } finally {
      setLoadingRunningTasks(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchRunningTasks();
    fetchAllDashboardCards();
  }, [user]);

  // WebSocket listeners for real-time updates
  useEffect(() => {
    if (!socket) return;

    const handleTimerStarted = (data) => {
      // Refetch running tasks when a timer is started
      fetchRunningTasks();
    };

    const handleGlobalTimerStarted = (data) => {
      // Refetch running tasks when a timer is started (global broadcast)
      fetchRunningTasks();
    };

    const handleTimerStopped = (data) => {
      // Remove the task from running tasks when timer stops
      setRunningTasks((prev) =>
        prev.filter((task) => task.taskId !== data.cardId)
      );
    };

    const handleGlobalTimerStopped = (data) => {
      // Remove the task from running tasks when timer stops (global broadcast)
      setRunningTasks((prev) =>
        prev.filter((task) => task.taskId !== data.cardId)
      );
    };

    const handleTimerDiscarded = (data) => {
      // Remove the task from running tasks when timer is discarded
      setRunningTasks((prev) =>
        prev.filter((task) => task.taskId !== data.cardId)
      );
    };

    const handleGlobalTimerDiscarded = (data) => {
      // Remove the task from running tasks when timer is discarded (global broadcast)
      setRunningTasks((prev) =>
        prev.filter((task) => task.taskId !== data.cardId)
      );
    };

    // Card-related events for dashboard refresh
    const handleCardEvent = () => {
      console.log("🔄 Real-time update: Refreshing dashboard cards");
      fetchAllDashboardCards();
    };

    socket.on("timer-started", handleTimerStarted);
    socket.on("global-timer-started", handleGlobalTimerStarted);
    socket.on("timer-stopped", handleTimerStopped);
    socket.on("global-timer-stopped", handleGlobalTimerStopped);
    socket.on("timer-discarded", handleTimerDiscarded);
    socket.on("global-timer-discarded", handleGlobalTimerDiscarded);

    // Register card event listeners
    socket.on("card-created", handleCardEvent);
    socket.on("card-updated", handleCardEvent);
    socket.on("card-deleted", handleCardEvent);
    socket.on("card-archived", handleCardEvent);
    socket.on("card-restored", handleCardEvent);
    socket.on("card-status-changed", handleCardEvent);
    socket.on("card-completion-toggled", handleCardEvent);
    socket.on("card-user-assigned", handleCardEvent);
    socket.on("card-user-unassigned", handleCardEvent);
    socket.on("card-moved-in", handleCardEvent);
    socket.on("card-moved-out", handleCardEvent);
    socket.on("cards-reordered", handleCardEvent);

    return () => {
      socket.off("timer-started", handleTimerStarted);
      socket.off("global-timer-started", handleGlobalTimerStarted);
      socket.off("timer-stopped", handleTimerStopped);
      socket.off("global-timer-stopped", handleGlobalTimerStopped);
      socket.off("timer-discarded", handleTimerDiscarded);
      socket.off("global-timer-discarded", handleGlobalTimerDiscarded);

      // Unregister card event listeners
      socket.off("card-created", handleCardEvent);
      socket.off("card-updated", handleCardEvent);
      socket.off("card-deleted", handleCardEvent);
      socket.off("card-archived", handleCardEvent);
      socket.off("card-restored", handleCardEvent);
      socket.off("card-status-changed", handleCardEvent);
      socket.off("card-completion-toggled", handleCardEvent);
      socket.off("card-user-assigned", handleCardEvent);
      socket.off("card-user-unassigned", handleCardEvent);
      socket.off("card-moved-in", handleCardEvent);
      socket.off("card-moved-out", handleCardEvent);
      socket.off("cards-reordered", handleCardEvent);
    };
  }, [socket, user]); // Added user to dependencies for fetchAllDashboardCards

  useEffect(() => {
    const fetchPinned = async () => {
      if (!user) return;
      try {
        const res = await userAPI.getPinned();
        const pinned = res.data?.pinned || [];
        setPinnedIds(pinned.map((p) => p._id));
      } catch (e) {
        console.error("Failed to fetch pinned projects", e);
      }
    };
    fetchPinned();
  }, [user]);

  const togglePin = (projectId) => {
    setPinnedIds((prev) => {
      const exists = prev.includes(projectId);
      const next = exists
        ? prev.filter((id) => id !== projectId)
        : [projectId, ...prev];
      // Persist to server (optimistic update)
      (async (nextIds, prevIds) => {
        try {
          await userAPI.setPinned(nextIds);
        } catch (err) {
          console.error("Failed to persist pinned projects", err);
          setPinnedIds(prevIds);
        }
      })(next, prev);
      return next;
    });
  };

  // Recently viewed projects (fetched from server, server applies 1 hour TTL)
  const [recentProjects, setRecentProjects] = useState([]);

  const refreshRecentlyViewed = async () => {
    if (!user) {
      setRecentProjects([]);
      return;
    }
    try {
      const res = await userAPI.getRecentlyViewed();
      const list = res.data?.recentlyViewed || [];
      setRecentProjects(list.map((it) => it.project).filter(Boolean));
    } catch (e) {
      console.error("Failed to fetch recently viewed projects", e);
      setRecentProjects([]);
    }
  };

  useEffect(() => {
    refreshRecentlyViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, projects]);

  // Get date from project for sorting
  const getProjectDate = (project) => {
    if (project.createdAt) return new Date(project.createdAt);
    if (project.startDate) return new Date(project.startDate);
    if (project.updatedAt) return new Date(project.updatedAt);
    return new Date(0); // fallback to epoch if no date
  };

  // Sort projects by date
  const sortProjectsByDate = (projectsToSort, order) => {
    return [...projectsToSort].sort((a, b) => {
      const dateA = getProjectDate(a);
      const dateB = getProjectDate(b);
      return order === "recent" ? dateB - dateA : dateA - dateB;
    });
  };

  // Get most recent/oldest date from a list of projects
  const getCategoryDate = (projectsList, order) => {
    if (!projectsList || projectsList.length === 0) return new Date(0);
    const dates = projectsList.map((p) => getProjectDate(p));
    return order === "recent" ? Math.max(...dates) : Math.min(...dates);
  };

  // Group projects by category
  const groupedProjects = useMemo(() => {
    if (!projects || projects.length === 0)
      return {
        allSections: [],
        uncategorizedProjects: [],
        pinnedProjects: [],
        workspaceProjects: [],
      };

    // Build pinned projects list (preserve pinned order from pinnedIds)
    const pinnedProjects = pinnedIds
      .map((id) => projects.find((p) => p._id === id))
      .filter(Boolean);

    // Other projects (include all projects). Pinned projects will appear both in Starred boards and in their original location.
    const otherProjects = projects;

    const categoryMap = new Map();
    const uncategorized = [];
    let workspaceCategory = null;

    otherProjects.forEach((project) => {
      if (project.category && project.category._id) {
        const catId = project.category._id;
        if (!categoryMap.has(catId)) {
          categoryMap.set(catId, {
            ...project.category,
            projects: [],
            isUncategorized: false,
          });
        }
        categoryMap.get(catId).projects.push(project);
      } else {
        uncategorized.push(project);
      }
    });

    // Extract workspace category if it exists
    const categories = Array.from(categoryMap.values());
    const nonWorkspaceCategories = categories.filter((cat) => {
      if (cat.name === "Workspace") {
        workspaceCategory = cat;
        return false;
      }
      return true;
    });

    // Sort projects within each category alphabetically by name
    nonWorkspaceCategories.forEach((cat) => {
      cat.projects = [...cat.projects].sort((a, b) => {
        const nameA = (a.name || "").toLowerCase();
        const nameB = (b.name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
    });
    if (workspaceCategory) {
      workspaceCategory.projects = [...workspaceCategory.projects].sort(
        (a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        }
      );
    }

    // Sort uncategorized projects alphabetically by name
    const sortedUncategorized = [...uncategorized].sort((a, b) => {
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });

    // Create allSections array that includes only non-workspace categories
    const allSections = [...nonWorkspaceCategories];

    // Sort all sections by the most recent/oldest project date
    allSections.sort((a, b) => {
      const dateA = getCategoryDate(a.projects, sortOrder);
      const dateB = getCategoryDate(b.projects, sortOrder);
      return sortOrder === "recent" ? dateB - dateA : dateA - dateB;
    });

    return {
      allSections,
      uncategorizedProjects: sortedUncategorized,
      pinnedProjects,
      workspaceProjects: workspaceCategory,
    };
  }, [projects, sortOrder, pinnedIds]);

  // Initialize all categories as expanded
  useEffect(() => {
    if (groupedProjects.allSections.length > 0) {
      setExpandedCategories((prev) => {
        const initialExpanded = { ...prev };
        let hasChanges = false;
        groupedProjects.allSections.forEach((section) => {
          if (initialExpanded[section._id] === undefined) {
            initialExpanded[section._id] = true;
            hasChanges = true;
          }
        });
        return hasChanges ? initialExpanded : prev;
      });
    }
  }, [groupedProjects.allSections]);

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // Close sort dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target)
      ) {
        setIsSortDropdownOpen(false);
      }
    };

    if (isSortDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSortDropdownOpen]);

  // Fetch cards due today
  useEffect(() => {
    const fetchCardsDueToday = async () => {
      try {
        setLoadingCards(true);
        console.log("📅 Fetching cards due today...");
        const response = await cardAPI.getCardsDueToday();
        console.log("📅 Cards due today response:", response.data);
        setCardsDueToday(response.data.cards || []);
      } catch (error) {
        console.error("❌ Error fetching cards due today:", error);
        setCardsDueToday([]);
      } finally {
        setLoadingCards(false);
      }
    };

    if (user) {
      fetchCardsDueToday();
    }
  }, [user]);

  // Fetch cards back date
  useEffect(() => {
    const fetchCardsBackDate = async () => {
      try {
        setLoadingBackDateCards(true);
        console.log("📅 Fetching cards back date...");
        const response = await cardAPI.getCardsBackDate();
        console.log("📅 Cards back date response:", response.data);
        setCardsBackDate(response.data.cards || []);
      } catch (error) {
        console.error("❌ Error fetching cards back date:", error);
        setCardsBackDate([]);
      } finally {
        setLoadingBackDateCards(false);
      }
    };

    if (user) {
      fetchCardsBackDate();
    }
  }, [user]);

  // Fetch upcoming cards
  useEffect(() => {
    const fetchCardsUpcoming = async () => {
      try {
        setLoadingUpcomingCards(true);
        console.log("📅 Fetching upcoming cards...");
        const response = await cardAPI.getCardsUpcoming();
        console.log("📅 Upcoming cards response:", response.data);
        setCardsUpcoming(response.data.cards || []);
      } catch (error) {
        console.error("❌ Error fetching upcoming cards:", error);
        setCardsUpcoming([]);
      } finally {
        setLoadingUpcomingCards(false);
      }
    };

    if (user) {
      fetchCardsUpcoming();
    }
  }, [user]);

  const handleModalClose = () => {
    setShowCreateModal(false);
  };

  const handleProjectCreated = () => {
    setShowCreateModal(false);
    // Only refresh projects after successful creation
    console.log("🔄 ProjectList: Refreshing projects after project creation");
    fetchProjects();
  };

  const handleCardClick = (card) => {
    // Navigate to the project board with the card open
    const projectId = card.project?._id || card.project;
    navigate(`/project/${projectId}/card/${card._id}`);
  };

  const formatDueDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Running Tasks Section - Admin Only */}
      {features.timeTracking && user?.role === "admin" && runningTasks.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 transition-colors duration-200 overflow-hidden">
          <div>
            <div className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 transition-colors duration-200 border-b">
              <div className="flex items-center space-x-2">
                <div className="h-[72px] flex flex-col justify-center px-6">
                  <h1 className="text-base font-semibold mb-1 dark:text-white leading-tight">Team Currently Working</h1>
                  <p className="text-sm dark:text-slate-400 leading-tight">
                    Displays all team members actively working on tasks in real time, including key task details and live progress, enabling better visibility, coordination, and productivity tracking.
                  </p>
                </div>
              </div>
            </div>

            {loadingRunningTasks ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-red-900"></div>
              </div>
            ) : runningTasks.length > 0 ? (
              <RunningTasksList
                tasks={runningTasks}
                title="Running Tasks"
                onTaskClick={(task) => {
                  navigate(`/project/${task.projectId}/card/${task.taskId}`);
                }}
              />
            ) : (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-gray-300 dark:text-slate-700 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-slate-500 text-sm">
                  No running tasks at the moment.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 transition-colors duration-200">
        <div>
          <div className="h-[72px] flex flex-col justify-center px-6">
            <h1 className="text-base font-semibold mb-1 dark:text-white leading-tight">Your Projects</h1>
            <p className="text-sm dark:text-slate-400 leading-tight">
              Manage your projects and collaborate with your team
            </p>
          </div>

          {/* Cards Due Today */}
          {user && (
            <div className="bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 px-4 py-2 transition-colors duration-200">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                <h2 className="text-base font-semibold dark:text-white">Due Today</h2>
                {cardsDueToday.length > 0 && (
                  <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-medium px-2 py-0.5 rounded-full">
                    {cardsDueToday.length}
                  </span>
                )}
              </div>

              {loadingCards ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : cardsDueToday.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {cardsDueToday.map((card) => (
                    <div
                      key={card._id}
                      onClick={() => handleCardClick(card)}
                      className="h-[60px] flex flex-col justify-between bg-gradient-to-br from-blue-50/70 to-indigo-50/70 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800/60 rounded-lg px-3 py-1.5 cursor-pointer hover:shadow-md hover:border-blue-400 dark:hover:border-blue-600 transition-all duration-200"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-slate-200 text-xs truncate flex-1 leading-tight">
                          {card.title}
                        </h3>
                        <span className="bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 text-[11px] font-medium px-2 py-0.5 rounded-full ml-1 flex-shrink-0 font-mono leading-none">
                          #{card.cardNumber || card._id?.slice(-4)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-slate-400 leading-tight">
                        <div className="flex items-center space-x-1 truncate max-w-[60%]">
                          <FolderOpen className="w-3 h-3 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                          <span className="truncate font-medium text-gray-700 dark:text-slate-300">{card.project?.name}</span>
                        </div>
                        <div className="flex items-center space-x-1 text-blue-600 dark:text-blue-400 font-medium flex-shrink-0">
                          <Calendar className="w-3 h-3" />
                          <span>Today</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <Clock className="w-8 h-8 text-gray-300 dark:text-slate-700 mx-auto mb-1" />
                  <p className="text-gray-500 dark:text-slate-500 text-xs">
                    No cards due today. You're all caught up! 🎉
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Cards Back Date */}
          {cardsBackDate.length > 0 && (
            <div className="bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 px-4 py-2 transition-colors duration-200">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="w-6 h-6 text-red-600 dark:text-red-400" />
                <h2 className="text-base font-semibold dark:text-white">Pending</h2>
                {cardsBackDate.length > 0 && (
                  <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-medium px-2 py-0.5 rounded-full">
                    {cardsBackDate.length}
                  </span>
                )}
              </div>

              {loadingBackDateCards ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                </div>
              ) : cardsBackDate.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {cardsBackDate.map((card) => (
                    <div
                      key={card._id}
                      onClick={() => handleCardClick(card)}
                      className="h-[60px] flex flex-col justify-between bg-gradient-to-br from-red-50/70 to-orange-50/70 dark:from-red-900/20 dark:to-orange-900/20 border border-red-200 dark:border-red-800/60 rounded-lg px-3 py-1.5 cursor-pointer hover:shadow-md hover:border-red-400 dark:hover:border-red-600 transition-all duration-200"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-slate-200 text-xs truncate flex-1 leading-tight">
                          {card.title}
                        </h3>
                        <span className="bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 text-[11px] font-medium px-2 py-0.5 rounded-full ml-1 flex-shrink-0 font-mono leading-none">
                          #{card.cardNumber || card._id?.slice(-4)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-slate-400 leading-tight">
                        <div className="flex items-center space-x-1 truncate max-w-[60%]">
                          <FolderOpen className="w-3 h-3 flex-shrink-0 text-red-600 dark:text-red-400" />
                          <span className="truncate font-medium text-gray-700 dark:text-slate-300">{card.project?.name}</span>
                        </div>
                        <div className="flex items-center space-x-1 text-red-600 dark:text-red-400 font-medium flex-shrink-0">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDueDate(card.dueDate)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <Clock className="w-8 h-8 text-gray-300 dark:text-slate-700 mx-auto mb-1" />
                  <p className="text-gray-500 dark:text-slate-500 text-xs">
                    No past due cards. Great job!
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Upcoming Cards */}
          {cardsUpcoming.length > 0 && (
            <div className="bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 px-4 py-2 rounded-b-lg transition-colors duration-200">
              <div className="flex items-center space-x-2 mb-2">
                <Calendar className="w-6 h-6 text-green-600 dark:text-green-400" />
                <h2 className="text-base font-semibold dark:text-white">Upcoming</h2>
                {cardsUpcoming.length > 0 && (
                  <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium px-2 py-0.5 rounded-full">
                    {cardsUpcoming.length}
                  </span>
                )}
              </div>

              {loadingUpcomingCards ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                </div>
              ) : cardsUpcoming.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {cardsUpcoming.map((card) => (
                    <div
                      key={card._id}
                      onClick={() => handleCardClick(card)}
                      className="h-[60px] flex flex-col justify-between bg-gradient-to-br from-green-50/70 to-emerald-50/70 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800/60 rounded-lg px-3 py-1.5 cursor-pointer hover:shadow-md hover:border-green-400 dark:hover:border-green-600 transition-all duration-200"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-slate-200 text-xs truncate flex-1 leading-tight">
                          {card.title}
                        </h3>
                        <span className="bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 text-[11px] font-medium px-2 py-0.5 rounded-full ml-1 flex-shrink-0 font-mono leading-none">
                          #{card.cardNumber || card._id?.slice(-4)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-slate-400 leading-tight">
                        <div className="flex items-center space-x-1 truncate max-w-[60%]">
                          <FolderOpen className="w-3 h-3 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
                          <span className="truncate font-medium text-gray-700 dark:text-slate-300">{card.project?.name}</span>
                        </div>
                        <div className="flex items-center space-x-1 text-green-600 dark:text-green-400 font-medium flex-shrink-0">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDueDate(card.dueDate)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <Calendar className="w-8 h-8 text-gray-300 dark:text-slate-700 mx-auto mb-1" />
                  <p className="text-gray-500 dark:text-slate-500 text-xs">
                    No upcoming cards scheduled.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Recently Viewed Section */}
      {recentProjects && recentProjects.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Clock className="w-6 h-6 text-gray-600 dark:text-slate-400" />
            <h2 className="text-base font-semibold dark:text-white transition-colors duration-200">Recently Viewed Project</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentProjects.map((project) => (
              <ProjectCard
                key={project._id}
                project={project}
                pinned={pinnedIds.includes(project._id)}
                onTogglePin={togglePin}
                onView={refreshRecentlyViewed}
              />
            ))}
          </div>
        </div>
      )}

      {groupedProjects.pinnedProjects &&
        groupedProjects.pinnedProjects.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Star className="w-6 h-6 text-gray-600 dark:text-slate-400" />
              <h2 className="text-base font-semibold dark:text-white transition-colors duration-200">Starred boards</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {groupedProjects.pinnedProjects.map((project) => (
                <ProjectCard
                  key={project._id}
                  project={project}
                  pinned={pinnedIds.includes(project._id)}
                  onTogglePin={togglePin}
                  onView={refreshRecentlyViewed}
                />
              ))}
            </div>
          </div>
        )}

      {/* Filter Bar */}
      {projects.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FolderClosed className="w-6 h-6 text-gray-600 dark:text-slate-400" />
            <h2 className="text-base font-semibold dark:text-white transition-colors duration-200">All Projects</h2>
          </div>
          <div className="flex items-center gap-2" ref={sortDropdownRef}>
            <SlidersHorizontal className="w-4 h-4 text-gray-500 dark:text-slate-400" />
            <div className="relative">
              <button
                type="button"
                onClick={() =>
                  setIsSortDropdownOpen((prevState) => !prevState)
                }
                className="flex items-center gap-2 text-sm border border-gray-300 dark:border-slate-700 rounded-lg px-3 py-1.5 bg-white dark:bg-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer min-w-[140px] justify-between transition-colors duration-200"
                aria-haspopup="listbox"
                aria-expanded={isSortDropdownOpen}
              >
                <span>
                  {sortOrder === "recent" ? "Recent First" : "Oldest First"}
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-gray-500 dark:text-slate-400 transition-transform duration-200 ${
                    isSortDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {isSortDropdownOpen && (
                <div className="absolute right-0 mt-2 w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg z-20 overflow-hidden transition-colors duration-200">
                  <button
                    type="button"
                    onClick={() => {
                      setSortOrder("recent");
                      setIsSortDropdownOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors ${
                      sortOrder === "recent" ? "bg-gray-50 dark:bg-slate-700/50 font-semibold" : "dark:text-slate-300"
                    }`}
                    role="option"
                    aria-selected={sortOrder === "recent"}
                  >
                    Recent First
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSortOrder("oldest");
                      setIsSortDropdownOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors ${
                      sortOrder === "oldest" ? "bg-gray-50 dark:bg-slate-700/50 font-semibold" : "dark:text-slate-300"
                    }`}
                    role="option"
                    aria-selected={sortOrder === "oldest"}
                  >
                    Oldest First
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Projects Grid - Grouped by Category */}
      {projects.length === 0 ? (
        <div className="text-center py-12">
          <FolderOpen className="w-16 h-16 text-secondary-300 dark:text-slate-700 mx-auto mb-4" />
          <h3 className="text-base font-semibold mb-2 dark:text-white transition-colors duration-200">No projects yet</h3>
          <p className="text-secondary-600 dark:text-slate-400 mb-6 transition-colors duration-200">
            {user?.role === "admin"
              ? "Create your first project to get started"
              : "You haven't been added to any projects yet"}
          </p>
          {user?.role === "admin" && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              Create Project
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6 pb-11">
          {/* Categorized Projects - Display in sections */}
          {groupedProjects.allSections.map((section) => (
            <div
              key={section._id}
              className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden transition-colors duration-200"
            >
              {/* Section Header */}
              <button
                onClick={() => toggleCategory(section._id)}
                className="w-full h-[54px] min-h-[54px] flex items-center justify-between px-5 bg-gray-50 dark:bg-slate-700/30 hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div
                    className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: section.color || "#6366f1" }}
                  >
                    <Folder className="w-3.5 h-3.5 text-white" />
                  </div>
                  <h2 className="text-base font-semibold dark:text-white transition-colors duration-200">{section.name}</h2>
                  <span className="text-sm text-gray-500 dark:text-slate-400 transition-colors duration-200">
                    ({section.projects.length})
                  </span>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 dark:text-slate-500 transition-transform duration-200 ${
                    expandedCategories[section._id] ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Section Projects */}
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  expandedCategories[section._id]
                    ? "max-h-[2000px] opacity-100"
                    : "max-h-0 opacity-0"
                }`}
              >
                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {section.projects.map((project) => (
                    <ProjectCard
                      key={project._id}
                      project={project}
                      pinned={pinnedIds.includes(project._id)}
                      onTogglePin={togglePin}
                      onView={refreshRecentlyViewed}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}

          {/* Uncategorized Projects - Display as individual cards at the bottom */}
          {groupedProjects.uncategorizedProjects.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {groupedProjects.uncategorizedProjects.map((project) => (
                <ProjectCard
                  key={project._id}
                  project={project}
                  pinned={pinnedIds.includes(project._id)}
                  onTogglePin={togglePin}
                  onView={refreshRecentlyViewed}
                />
              ))}
            </div>
          )}

          {/* Workspace Projects - Display at the bottom */}
          {groupedProjects.workspaceProjects && (
            <div
              key={groupedProjects.workspaceProjects._id}
              className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden transition-colors duration-200"
            >
              {/* Section Header */}
              <button
                onClick={() =>
                  toggleCategory(groupedProjects.workspaceProjects._id)
                }
                className="w-full h-[54px] min-h-[54px] flex items-center justify-between px-5 bg-gray-50 dark:bg-slate-700/30 hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div
                    className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor:
                        groupedProjects.workspaceProjects.color || "#6366f1",
                    }}
                  >
                    <Folder className="w-3.5 h-3.5 text-white" />
                  </div>
                  <h2 className="text-base font-semibold dark:text-white transition-colors duration-200">
                    {groupedProjects.workspaceProjects.name}
                  </h2>
                  <span className="text-sm text-gray-500 dark:text-slate-400 transition-colors duration-200">
                    ({groupedProjects.workspaceProjects.projects.length})
                  </span>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 dark:text-slate-500 transition-transform duration-200 ${
                    expandedCategories[groupedProjects.workspaceProjects._id]
                      ? "rotate-180"
                      : ""
                  }`}
                />
              </button>

              {/* Section Projects */}
              <div
                className={`transition-all duration-300 ${
                  expandedCategories[groupedProjects.workspaceProjects._id]
                    ? "max-h-auto opacity-100"
                    : "max-h-0 opacity-0"
                }`}
              >
                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedProjects.workspaceProjects.projects.map((project) => (
                    <ProjectCard
                      key={project._id}
                      project={project}
                      pinned={pinnedIds.includes(project._id)}
                      onTogglePin={togglePin}
                      onView={refreshRecentlyViewed}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* View All Closed Projects Button - Admin Only */}
      {user?.role === "admin" && (
        <div className="pb-8">
          <button
            onClick={() => navigate("/archived-projects")}
            className="group flex items-center gap-2 px-4 py-2.5 text-gray-700 dark:text-slate-300 border border-gray-700 dark:border-slate-600 font-semibold rounded-lg hover:text-blue-700 dark:hover:text-blue-400 hover:border-blue-700 dark:hover:border-blue-400 transition-all duration-300"
          >
            <Archive className="w-4 h-4" />
            <span>View All Closed Projects</span>
          </button>
        </div>
      )}

      {/* Create Project Modal */}
      {showCreateModal && (
        <CreateProjectModal
          onClose={handleModalClose}
          onSuccess={handleProjectCreated}
        />
      )}
    </div>
  );
};

const ProjectCard = ({
  project,
  pinned = false,
  onTogglePin = () => {},
  onView = () => {},
}) => {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const { user } = useUser();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showDropdown]);

  const handleEditProject = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdown(false);
    navigate(`/project/${project._id}/edit`);
  };

  const handleDropdownToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  return (
    <Link
      to={`/project/${project._id}`}
      onClick={() => {
        if (user) {
          userAPI
            .recordRecentlyViewed(project._id)
            .then(() => {
              try {
                onView();
              } catch (e) {}
            })
            .catch((err) =>
              console.error("Failed to record recently viewed", err)
            );
        }
      }}
      className="group block bg-white dark:bg-slate-800 rounded-lg shadow-sm hover:shadow-xl transition-all duration-300 border border-secondary-200 dark:border-slate-700 hover:border-primary-200 dark:hover:border-blue-700 overflow-hidden"
    >
      {/* Card Header with Gradient */}
      <div className="px-6 h-[44px] min-h-[44px] flex items-center bg-[#f1f2f4] dark:bg-slate-700/50 transition-colors duration-200">
        <div className="flex items-center justify-between w-full">
          <div className="flex-1">
            <h3 className="text-base font-semibold dark:text-white transition-colors duration-200 truncate">{project.name}</h3>
          </div>
          <div className="relative flex items-center gap-2" ref={dropdownRef}>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // toggle pin
                onTogglePin(project._id);
              }}
              title={pinned ? "Unpin project" : "Pin project"}
              className="p-1 rounded-lg dark:hover:bg-slate-600 transition-colors duration-200 hover:text-yellow-500 "
            >
              <Star
                className={`w-4 h-4 ${
                  pinned ? "fill-yellow-500 text-yellow-500" : "dark:text-slate-400"
                }`}
              />
            </button>

            <button
              onClick={handleDropdownToggle}
              className="p-1 rounded-lg hover:bg-white dark:hover:bg-slate-600 hover:bg-opacity-20 transition-colors duration-200"
            >
              <MoreVertical className="w-4 h-4 dark:text-slate-400" />
            </button>

            {/* Dropdown Menu */}
            {showDropdown && (
              <div className="absolute right-0 top-8 mt-2 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 py-1 z-50">
                <button
                  onClick={handleEditProject}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  <span>Project Details</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card Body */}
      <div className="h-[100px] min-h-[100px] flex flex-col justify-between px-6 py-3">
        <div className="flex items-center justify-between text-sm text-secondary-500 dark:text-slate-400 transition-colors duration-200">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Users className="w-4 h-4" />
              <span className="font-medium">
                {project.members?.length || 0} members
              </span>
            </div>
            <div className="flex -space-x-2">
              {project.members?.slice(0, 4).map((member, index) => (
                <div
                  key={member.user?._id || member.user || index}
                  className="border-2 border-white dark:border-slate-800 shadow-sm rounded-full"
                >
                  <Avatar user={member.user} size="xs" showTooltip={true} />
                </div>
              ))}
              {project.members && project.members.length > 4 && (
                <div className="w-6 h-6 bg-secondary-300 dark:bg-slate-700 rounded-full border-2 border-white dark:border-slate-800 flex items-center justify-center text-[10px] text-secondary-600 dark:text-slate-300 font-medium shadow-sm">
                  +{project.members.length - 4}
                </div>
              )}
            </div>
          </div>
          <span
            className={getStatusBadgeClasses(
              "projectStatus",
              project.projectStatus
            )}
          >
            {getProjectStatusColors(project.projectStatus).label}
          </span>
        </div>

        {/* Team Members */}
        <div className="flex items-center justify-between">
          <div className="text-left">
            <div className="text-xs text-secondary-500 dark:text-slate-500">Start Date</div>
            <div className="text-sm font-medium text-secondary-900 dark:text-slate-200 transition-colors duration-200">
              {formatDate(project.startDate)}
            </div>
          </div>

          {project.endDate ? (
            <div className="text-left">
              <div className="text-xs text-secondary-500 dark:text-slate-500">End Date</div>
              <div className="text-sm font-medium text-secondary-900 dark:text-slate-200 transition-colors duration-200">
                {formatDate(project.endDate)}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Card Footer */}
      <div className="px-6 h-[40px] min-h-[40px] flex items-center border-t border-secondary-100 dark:border-slate-700 transition-colors duration-200">
        <div className="flex items-center justify-between text-xs text-secondary-500 dark:text-slate-500 transition-colors duration-200 w-full leading-none">
          <span className="font-medium truncate">
            {project.clientName || "No client"}
          </span>
          <span className="text-primary-600 dark:text-blue-400 font-medium group-hover:text-primary-700 dark:group-hover:text-blue-300 flex-shrink-0">
            View Project
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ProjectList;
